import React from 'react'

import PropTypes from 'prop-types'
import {Flex, SimpleGrid, useMultiStyleConfig} from '@chakra-ui/react'
import {MenuCardGroup} from '../list-menu/menu-card-group'
import {MenuCard} from '../list-menu/menu-card'
import {getGlobalLink} from '../../utils/contentstack-utils'
import {useIntl} from 'react-intl'
import {AuthHelpers, useAuthHelper, useCustomerType} from '@salesforce/commerce-sdk-react'
import {AccountIcon, LogoutIcon, FavouriteIcon} from '../icons'
import {useGigya} from '../../context/gigya'
import {getMrtApiBasepath} from '../../ssr/helpers'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useCorporateLayout from '../../hooks/use-corporate-layout'

const DrawerMenuFirstLevel = ({
    primarycategories,
    categoriesData,
    onWishlistClick,
    headerLinks,
    setSelectedPrimaryCategoryId,
}) => {
    const styles = useMultiStyleConfig('DrawerMenu')
    const intl = useIntl()
    const {isRegistered} = useCustomerType()
    const {showCorporateLayout} = useCorporateLayout()
    const simpleLinks = primarycategories.filter(
        (primarycategory) => primarycategory?._content_type_uid === 'firstlevelsimplelink',
    )

    const promosFirstLevel = primarycategories.filter(
        (primarycategory) =>
            primarycategory?.second_level?.layout === 'Promo/Gift' &&
            primarycategory?.second_level?.show_group_in_first_level,
    )

    const restOfCategories = primarycategories.filter(
        (primarycategory) =>
            primarycategory?._content_type_uid !== 'firstlevelsimplelink' &&
            !(
                primarycategory?.second_level?.layout === 'Promo/Gift' &&
                primarycategory?.second_level?.show_group_in_first_level
            ),
    )

    return (
        <SimpleGrid {...styles.firstLevelGrid}>
            {!showCorporateLayout && (
                <>
                    {simpleLinks.map((simpleLink) => {
                        const {url, label, isAbsolute} = getGlobalLink({
                            link: simpleLink?.redirects,
                            categoriesData,
                        })
                        return (
                            <MenuCard
                                key={simpleLink.uid}
                                title={label}
                                image={simpleLink?.mobile_image}
                                variant="simpleLink"
                                isAbsolute={isAbsolute}
                                customTitleColor={simpleLink?.label_hex_color}
                                url={url}
                                isLink
                            />
                        )
                    })}
                    <SimpleGrid {...styles.firstLevelSecondaryGrid}>
                        {restOfCategories.map((mainCategory) => {
                            const {label} = getGlobalLink({
                                link: mainCategory.redirects,
                                categoriesData,
                            })
                            return (
                                <MenuCard
                                    key={mainCategory.uid}
                                    title={label}
                                    image={mainCategory?.mobile_image}
                                    handleClick={() =>
                                        setSelectedPrimaryCategoryId(mainCategory.uid)
                                    }
                                />
                            )
                        })}
                    </SimpleGrid>
                    {promosFirstLevel.map((promo) => (
                        <MenuCardGroup
                            key={promo.uid}
                            categoriesData={categoriesData}
                            type={'firstLevelPromo'}
                            group={{
                                ...promo.second_level.promos_group[0],
                                redirects: promo.redirects,
                                viewAllRedirects: promo.second_level.footer?.redirects,
                                view_all_label:
                                    promo.second_level.footer.show_explore_all &&
                                    promo.second_level.footer.explore_all_label,
                            }}
                            compact
                        />
                    ))}
                </>
            )}
            <SimpleGrid {...styles.headerLinks}>
                {isRegistered && (
                    <MenuCard
                        title={intl.formatMessage({
                            defaultMessage: 'My Account',
                            id: 'header.popover.title.my_account',
                        })}
                        variant="secondary"
                        url={`/account`}
                        isLink
                        svg={
                            <Flex {...styles.svgContainer}>
                                <AccountIcon {...styles.headerIconSize} />
                            </Flex>
                        }
                    />
                )}
                {!showCorporateLayout && (
                    <MenuCard
                        title={intl.formatMessage({
                            id: 'global.wishlist',
                            defaultMessage: 'Wishlist',
                        })}
                        variant="secondary"
                        handleClick={onWishlistClick}
                        svg={
                            <Flex {...styles.svgContainer}>
                                <FavouriteIcon {...styles.headerIconSize} />
                            </Flex>
                        }
                    />
                )}
                {!showCorporateLayout &&
                    headerLinks.map((headerLink) => (
                        <MenuCard
                            key={headerLink._metadata.uid}
                            title={headerLink.link.title}
                            url={headerLink.link.href}
                            image={headerLink.icon}
                            isAbsolute={headerLink?.is_external_url}
                            variant="secondary"
                            isLink
                        />
                    ))}
                {isRegistered && <LogoutCard />}
            </SimpleGrid>
        </SimpleGrid>
    )
}

DrawerMenuFirstLevel.displayName = 'DrawerMenuFirstLevel'

DrawerMenuFirstLevel.propTypes = {
    primarycategories: PropTypes.array,
    setSelectedPrimaryCategoryId: PropTypes.func,
    categoriesData: PropTypes.object,
    onWishlistClick: PropTypes.func,
    headerLinks: PropTypes.array,
}

export {DrawerMenuFirstLevel}

function LogoutCard() {
    const styles = useMultiStyleConfig('DrawerMenu')
    const intl = useIntl()
    const navigate = useNavigation()

    const {gigyaObject} = useGigya()
    const logout = useAuthHelper(AuthHelpers.Logout)

    const onSignoutClick = async () => {
        gigyaObject && gigyaObject.accounts && gigyaObject.accounts.logout()
        await logout.mutateAsync()

        const endpoint = origin + getMrtApiBasepath('/logout')
        await fetch(`${endpoint}`).then(() => {
            setTimeout(() => {
                navigate('/login')
            }, 500)
        })
    }

    return (
        <MenuCard
            title={intl.formatMessage({
                defaultMessage: 'Log out',
                id: 'header.popover.action.log_out',
            })}
            variant="secondary"
            handleClick={() => onSignoutClick()}
            svg={
                <Flex {...styles.svgContainer}>
                    <LogoutIcon {...styles.headerIconSize} />
                </Flex>
            }
        />
    )
}
